import { message } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { useRecoilState } from 'recoil'
import { getOrgChart } from '../../../api/aopsHR.api'
import AopsBox from '../../../components/apos/AopsBox'
import AopsSimpleHeader from '../../../components/apos/AopsSimpleHeader'
import SectionHeader from '../../../components/apos/SectionHeader'
import { isAopsChiefState } from '../../../store/aops'
import { userState } from '../../../store/user'
import PersonDetailModal from './components/PersonDetailModal'
import PersonFormModal from './components/PersonFormModal'
import './orgChart.scss'
import PersonnelGrid from './ui/PersonnelGrid'
import StudioSidebar from './ui/StudioSidebar'
import TeamTabs from './ui/TeamTabs'

const OrgChartPage = () => {
  const [selectedStudioId, setSelectedStudioId] = useState(null)
  const [selectedTeamId, setSelectedTeamId] = useState(null)
  const [selectedPersonId, setSelectedPersonId] = useState(null)
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [orgChartData, setOrgChartData] = useState({ studios: [] })
  const [user] = useRecoilState(userState)
  const [isChief] = useRecoilState(isAopsChiefState)
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false)

  // 스튜디오 목록 (사이드바용)
  const studios = useMemo(
    () =>
      orgChartData.studios?.map((studio) => ({
        id: studio.id,
        name: studio.nameKo || studio.name || '',
      })) || [],
    [orgChartData.studios],
  )

  // 선택된 스튜디오의 팀 목록
  const selectedStudio = useMemo(
    () => orgChartData.studios?.find((s) => s.id === selectedStudioId),
    [orgChartData.studios, selectedStudioId],
  )

  const teams = useMemo(() => {
    if (!selectedStudio) return [{ id: null, name: '전체' }]
    return [
      { id: null, name: '전체' },
      ...(selectedStudio.teams?.map((team) => ({
        id: team.id,
        name: team.nameKo || team.name || '',
      })) || []),
    ]
  }, [selectedStudio])

  // 필터링된 인원 목록
  const filteredPersonnel = useMemo(() => {
    const result = []
    orgChartData.studios?.forEach((studio) => {
      // 스튜디오 필터링
      if (selectedStudioId && studio.id !== selectedStudioId) return

      studio.teams?.forEach((team) => {
        // 팀 필터링
        if (selectedTeamId && team.id !== selectedTeamId) return

        team.employees?.forEach((employee) => {
          result.push({
            id: employee.userId,
            employeeId: employee.employeeId,
            name: employee.name,
            position: employee.position,
            part: employee.part,
            email: employee.email,
            studio: studio.nameKo || studio.name || '',
            team: team.nameKo || team.name || '',
          })
        })
      })
    })
    return result
  }, [orgChartData.studios, selectedStudioId, selectedTeamId])

  const handlePersonClick = (person) => {
    setSelectedPersonId(person.id)
    setSelectedEmployeeId(person.employeeId)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedPersonId(null)
    setSelectedEmployeeId(null)
  }

  const handleRegisterClick = () => {
    if (isChief) {
      setIsRegisterModalOpen(true)
    } else {
      message.warning('인사정보 등록 권한이 없습니다.')
      return
    }
  }

  const handleCloseRegisterModal = () => {
    if (isChief) {
      setIsRegisterModalOpen(false)
    } else {
      return
    }
  }

  const handleRegisterSuccess = () => {
    setIsRegisterModalOpen(false)
    // 등록 후 조직도 새로고침
    handleGetOrgChart()
  }

  const handleGetOrgChart = async () => {
    try {
      const res = await getOrgChart(user?.id)
      if (res.status === 200) {
        // console.log('getOrgChart response::', res.data)
        setOrgChartData(res.data)
      } else {
        console.log('error::', res.data)
        message.error(res.data.message)
      }
    } catch (error) {
      console.error('error::', error)
      message.error(error.message)
    }
  }
  useEffect(() => {
    if (isChief) {
      handleGetOrgChart()
    } else {
      return
    }
  }, [user?.id, isChief])

  // 조직도 데이터 로드 후 첫 번째 스튜디오 자동 선택
  useEffect(() => {
    if (orgChartData.studios?.length > 0 && selectedStudioId === null) {
      setSelectedStudioId(orgChartData.studios[0].id)
    }
  }, [orgChartData.studios, selectedStudioId])

  return (
    <div>
      <AopsSimpleHeader title="인사 관리" />

      <div className="org-chart-container">
        <SectionHeader
          title="조직도"
          subtitle="전사 직원들의 인사정보를 들어가서 확인할 수 있어요"
          buttonText="인사정보 등록"
          onButtonClick={handleRegisterClick}
          iconType="arrow-right"
          buttonDisabled={!isChief}
        />

        <AopsBox
          style={{
            padding: '24px 24px 44px 24px',
          }}
          title="전사 조직도"
          borderBottom={true}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '16px',
              marginTop: '24px',
            }}
          >
            <StudioSidebar
              studios={studios}
              selectedStudio={selectedStudioId}
              onStudioSelect={(studioId) => {
                setSelectedStudioId(studioId)
                setSelectedTeamId(null)
              }}
            />

            <div className="org-chart-main">
              <TeamTabs
                teams={teams}
                selectedTeam={selectedTeamId}
                onTeamSelect={setSelectedTeamId}
              />

              <PersonnelGrid
                personnel={filteredPersonnel}
                onPersonClick={handlePersonClick}
              />
            </div>
          </div>
        </AopsBox>
      </div>

      {/* 개인 디테일 인사 정보 */}
      <PersonDetailModal
        open={isModalOpen}
        onClose={handleCloseModal}
        personId={selectedPersonId}
        employeeId={selectedEmployeeId}
        onSuccess={handleGetOrgChart}
      />

      <PersonFormModal
        open={isRegisterModalOpen}
        onClose={handleCloseRegisterModal}
        onSuccess={handleRegisterSuccess}
        isEditMode={false}
        userId={user?.id}
      />
    </div>
  )
}

export default OrgChartPage
